import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import toast from 'react-hot-toast';

const ADD_PARTICIPANT_DURING_CALL = gql`
  mutation AddParticipantDuringCall($conferenceId: ID!, $participant: ParticipantInput!) {
    addParticipantDuringCall(conferenceId: $conferenceId, participant: $participant) {
      id
      participants {
        id
        name
        email
        type
      }
    }
  }
`;

interface AddParticipantModalProps {
  conferenceId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function AddParticipantModal({ conferenceId, isOpen, onClose }: AddParticipantModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const [addParticipant, { loading }] = useMutation(ADD_PARTICIPANT_DURING_CALL);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: { name?: string; email?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (email && !validateEmail(email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});

    try {
      await addParticipant({
        variables: {
          conferenceId,
          participant: {
            type: 'GUEST',
            name: name.trim(),
            email: email.trim() || null,
            phone: phone.trim() || null,
            notifyEmail: !!email.trim(),
            notifySms: false,
            notifyPush: false,
          },
        },
      });
      
      toast.success(`Invitation sent to ${name}`);
      setName('');
      setEmail('');
      setPhone('');
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add participant');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add Participant</h2>
          <p className="text-sm text-gray-500 mt-1">
            Invite someone to join this meeting
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter participant name"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email address"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              An invitation link will be sent to this email
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone (optional)
            </label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter phone number"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
